import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Button, Input } from '../atoms';
import { Modal, useToast } from '../organisms';

/**
 * CalendarSubscribe — "Subscribe in your calendar" control for /calendar.
 *
 * Wires the REAL backend feed-token surface:
 *   • GET  /api/calendar/feed-token         → reveal-or-lazily-create the caller's
 *                                              token + the https/webcal subscribe URLs
 *   • POST /api/calendar/feed-token/rotate  → reset the link (invalidate + reissue)
 *
 * The URL points at the PUBLIC read-only .ics feed (GET /api/calendar/feed/<token>.ics),
 * authenticated solely by the secret token — the standard pattern Outlook / Google /
 * Apple honor for "Add calendar from URL". No OAuth, no token storage churn.
 *
 * Material-Design: uses the shared Modal (M3 Dialog) + Button/Input atoms + design
 * tokens — no new UI framework.
 */

interface FeedToken {
  token: string;
  url: string;
  webcal_url: string;
}

const FEED_TOKEN_KEY = ['calendar-feed-token'] as const;

export function CalendarSubscribe() {
  const [open, setOpen] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [copied, setCopied] = useState<'url' | 'webcal' | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Lazy: only fetch (and thereby mint) the token once the dialog is opened.
  const tokenQuery = useQuery<FeedToken>({
    queryKey: FEED_TOKEN_KEY,
    queryFn: ({ signal }) => api.get<FeedToken>('/api/calendar/feed-token', signal),
    enabled: open,
    staleTime: Infinity,
  });

  const rotateMut = useMutation({
    mutationFn: () => api.post<FeedToken>('/api/calendar/feed-token/rotate'),
    onSuccess: (data) => {
      queryClient.setQueryData(FEED_TOKEN_KEY, data);
      setConfirmingReset(false);
      toast({ variant: 'success', message: 'Subscription link reset. The old link no longer works.' });
    },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not reset the link.' }),
  });

  async function copy(value: string, which: 'url' | 'webcal') {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      setTimeout(() => setCopied((c) => (c === which ? null : c)), 1600);
      toast({ variant: 'success', message: 'Copied to clipboard.' });
    } catch {
      toast({ variant: 'error', message: 'Copy failed — select the text and copy manually.' });
    }
  }

  function close() {
    setOpen(false);
    setConfirmingReset(false);
    setCopied(null);
  }

  const data = tokenQuery.data;

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>Subscribe</Button>

      <Modal
        open={open}
        onClose={close}
        title="Subscribe in your calendar"
        size="lg"
        footer={<Button variant="secondary" onClick={close}>Done</Button>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
            Add this read-only feed to Outlook, Google Calendar, or Apple Calendar and your
            deadlines, hearings, tax-bill dates, and reminders — scoped to your book — stay in
            sync. The link is a private secret; anyone with it can read your calendar, so don't
            share it. Use <strong>Reset link</strong> to revoke it.
          </p>

          {tokenQuery.isLoading && <p style={{ color: 'var(--color-ink-3)' }}>Loading your subscription link…</p>}
          {tokenQuery.isError && (
            <p style={{ color: 'var(--color-status-red)' }}>Could not load your subscription link. Please try again.</p>
          )}

          {data && (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
                <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink-2)' }}>Feed URL (https)</span>
                <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                  <Input value={data.url} readOnly onFocus={(e) => e.currentTarget.select()} style={{ flex: 1, fontFamily: 'var(--font-mono, monospace)', fontSize: 'var(--fs-xs)' }} />
                  <Button variant="primary" size="sm" onClick={() => copy(data.url, 'url')}>{copied === 'url' ? 'Copied' : 'Copy'}</Button>
                </div>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
                <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink-2)' }}>One-click (webcal)</span>
                <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                  <Input value={data.webcal_url} readOnly onFocus={(e) => e.currentTarget.select()} style={{ flex: 1, fontFamily: 'var(--font-mono, monospace)', fontSize: 'var(--fs-xs)' }} />
                  <Button variant="secondary" size="sm" onClick={() => copy(data.webcal_url, 'webcal')}>{copied === 'webcal' ? 'Copied' : 'Copy'}</Button>
                  <a href={data.webcal_url} style={{ textDecoration: 'none' }}>
                    <Button variant="ghost" size="sm">Open</Button>
                  </a>
                </div>
              </label>

              <div style={{ background: 'var(--color-surface-2, rgba(0,0,0,0.03))', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)', padding: 'var(--sp-3)' }}>
                <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>How to add it</h3>
                <ul style={{ margin: 0, paddingLeft: 'var(--sp-4)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-xs)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
                  <li><strong>Google Calendar:</strong> Other calendars → + → From URL → paste the https URL.</li>
                  <li><strong>Outlook:</strong> Add calendar → Subscribe from web → paste the https URL.</li>
                  <li><strong>Apple Calendar:</strong> File → New Calendar Subscription → paste the webcal URL (or click Open).</li>
                </ul>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--sp-3)' }}>
                {!confirmingReset ? (
                  <Button variant="ghost" size="sm" onClick={() => setConfirmingReset(true)}>Reset link…</Button>
                ) : (
                  <>
                    <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>Reset now? Existing subscriptions will stop updating.</span>
                    <Button variant="danger" size="sm" onClick={() => rotateMut.mutate()} disabled={rotateMut.isPending}>
                      {rotateMut.isPending ? 'Resetting…' : 'Reset link'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmingReset(false)} disabled={rotateMut.isPending}>Cancel</Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}
